import { AxiosProgressEvent } from "axios";
import { Loader, Upload } from "lucide-react";
import {
  ChangeEvent,
  ComponentProps,
  FC,
  MouseEvent,
  ReactNode,
  useCallback,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { createImage, createTrack, createVideo } from "@/data/api";
import { useDialogState } from "@/hooks/use-dialog-state";
import {
  FileUploadAcceptTypes,
  MaxFileSizeBytes,
  pollForVideoProcessing,
  uploadImageToCloudflare,
  uploadTrackToCloudflareR2,
  uploadVideoToCloudflareWithTus,
} from "@/lib/uploads";

type UploadedFile = {
  file_name: string;
  url: string;
};

export type FileUploadInputInnerProps = ComponentProps<"input"> & {
  openFilePicker: (event: MouseEvent) => void;
  isUploading: boolean;
  uploadedBytes: number;
  totalBytes: number;
  totalFileCount: number;
  currentFileCount: number;
};

type MultiFileUploadButtonProps = {
  fileType: "image" | "video" | "audio";
  onFinish?: (uploadedFiles: UploadedFile[]) => void;
  children?: (props: FileUploadInputInnerProps) => ReactNode;
  className?: string;
};

export const MultiFileUploadButton: FC<MultiFileUploadButtonProps> = ({
  fileType,
  onFinish,
  children,
  className,
}) => {
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const { openDialog } = useDialogState();
  const [isUploading, setUploading] = useState(false);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [totalFileCount, setTotalFileCount] = useState(0);
  const [currentFileCount, setCurrentFileCount] = useState(0);

  const openFilePicker = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      hiddenInputRef.current?.click();
    },
    [hiddenInputRef],
  );

  const handleProgressEvent = (progressEvent: AxiosProgressEvent) => {
    if (progressEvent.lengthComputable && progressEvent.total) {
      setUploadedBytes(progressEvent.loaded);
      setTotalBytes(progressEvent.total);
    }
  };

  const handleTusProgressEvent = (
    tusUploadedBytes: number,
    tusTotalBytes: number,
  ) => {
    setUploadedBytes(tusUploadedBytes);
    setTotalBytes(tusTotalBytes);
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files) return;

    setTotalFileCount(files.length);
    setCurrentFileCount(0);
    setUploadedBytes(0);
    setTotalBytes(0);
    setUploading(true);

    const uploadedFiles: UploadedFile[] = [];

    // forEach is not awaited, make sure to use a for loop
    for (const file of files) {
      setCurrentFileCount((prev) => prev + 1);

      if (
        (fileType === "image" &&
          file.size > MaxFileSizeBytes.CloudflareImage) ||
        (fileType === "video" &&
          file.size > MaxFileSizeBytes.CloudflareTusVideo)
      ) {
        openDialog({ name: "asset-size" });
        setUploading(false);
        return;
      }

      let videoId: number | undefined;

      try {
        if (fileType === "image") {
          const upload = uploadImageToCloudflare(file, handleProgressEvent);

          toast.promise(upload, {
            loading: `Uploading Image…`,
            description: "Do not close this browser tab or navigate away",
            success: () => {
              return {
                message: "Image Uploaded",
                description: file.name,
              };
            },
            error: () => {
              return {
                message: "Image Upload Failed",
                description: file.name,
              };
            },
          });

          const { result } = await upload;
          const image = await createImage({
            cloudflare_image_id: result.id,
            file_name: file.name,
          });

          uploadedFiles.push({
            url: image.url,
            file_name: image.file_name,
          });
        } else if (fileType === "video") {
          const upload = uploadVideoToCloudflareWithTus(
            file,
            handleTusProgressEvent,
          );

          toast.promise(upload, {
            loading: `Uploading Video…`,
            description: "Do not close this browser tab or navigate away",
            success: () => {
              return {
                message: "Video Uploaded",
                description: file.name,
              };
            },
            error: () => {
              return {
                message: "Video Upload Failed",
                description: file.name,
              };
            },
          });

          const { uid } = await upload;
          if (!uid) {
            throw new Error("Failed to get Cloudflare video UID");
          }

          const video = await createVideo({
            cloudflare_video_uid: uid,
            file_name: file.name,
          });

          uploadedFiles.push({
            url: video.hls_url,
            file_name: video.file_name,
          });

          videoId = video.id;
        } else if (fileType === "audio") {
          const upload = uploadTrackToCloudflareR2(file, handleProgressEvent);

          toast.promise(upload, {
            loading: `Uploading Audio…`,
            description: "Do not close this browser tab or navigate away",
            success: () => {
              return {
                message: "Audio Uploaded",
                description: file.name,
              };
            },
            error: () => {
              return {
                message: "Audio Upload Failed",
                description: file.name,
              };
            },
          });

          const { result } = await upload;
          const track = await createTrack({
            object_key: result.object_key,
            uid: result.uid,
            file_name: file.name,
            file_size_bytes: file.size,
          });

          uploadedFiles.push({
            url: track.url,
            file_name: track.file_name,
          });
        }

        if (fileType === "video") {
          // videos need a few seconds for the preview image to process
          // if the refresh happens too quickly, the thumbnail would be broken
          if (videoId) {
            const isReady = pollForVideoProcessing(videoId);

            toast.promise(isReady, {
              loading: "Processing Video…",
              description: file.name,
              success: () => {
                return {
                  message: "Video Uploaded and Processed",
                  description: file.name,
                };
              },
              error: () => {
                return {
                  message: "Video Status Unknown",
                  description: file.name,
                };
              },
            });
          }
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`, {
          description: error?.toString() || (error as string),
        });
      }
    }

    onFinish?.(uploadedFiles);

    setUploading(false);
  };

  return (
    <div>
      {children ? (
        children({
          openFilePicker,
          uploadedBytes,
          totalBytes,
          isUploading,
          totalFileCount,
          currentFileCount,
          className,
        })
      ) : (
        <FileUploadInputInner
          openFilePicker={openFilePicker}
          uploadedBytes={uploadedBytes}
          totalBytes={totalBytes}
          isUploading={isUploading}
          totalFileCount={totalFileCount}
          currentFileCount={currentFileCount}
          className={className}
        />
      )}
      <Input
        ref={hiddenInputRef}
        type="file"
        className="hidden"
        onChange={handleFileChange}
        multiple
        accept={FileUploadAcceptTypes[fileType]}
      />
    </div>
  );
};

const FileUploadInputInner: FC<FileUploadInputInnerProps> = ({
  openFilePicker,
  isUploading,
  uploadedBytes,
  totalBytes,
  totalFileCount,
  currentFileCount,
}) => {
  if (isUploading && totalBytes > 0) {
    const uploadProgress = Math.round((uploadedBytes / totalBytes) * 100);

    return (
      <Button variant="secondary" disabled className="w-[392px]" type="button">
        <Progress value={uploadProgress} className="animate-pulse" />
        <span className="text-right">
          {uploadProgress}% -{" "}
          {Math.ceil(uploadedBytes / 1024 / 1024).toLocaleString()}/
          {Math.ceil(totalBytes / 1024 / 1024).toLocaleString()}MB - Files{" "}
          {currentFileCount} of {totalFileCount}
        </span>
      </Button>
    );
  }

  return (
    <Button
      onClick={openFilePicker}
      className="w-[192px]"
      type="button"
      disabled={isUploading}
    >
      {isUploading ? <Loader className="animate-spin" /> : <Upload />} Batch
      Upload Files
    </Button>
  );
};
